'use client';

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useIdGenerator } from './use-session';
import { apiClient } from '@/lib/api-client';
import { isImpersonating } from '@/lib/impersonate-client';
import { toast } from '@/lib/toast';
import { UploadedFile, SessionContent } from '@/types/chat';
import { compareOdmXml, describeDiff, type ElementDiff } from '@/lib/odm-diff';
import { mergeXmlFragment } from '@/lib/odm/merge-xml-fragment';
import { validateThreePhOdm } from '@/lib/odm/threeph-cloud';
import { suggestOdmArtifactBasename, extractOdmVersionFromFilename } from '@/lib/odm/study-version-meta';
import { getActiveStudyCodeForRegistration } from '@/lib/study-thread-bindings';
import { useChangeContextOptional, type NewChange } from '@/contexts';
import type { ToolId } from '@/lib/ai/prompts';
export type ContentTabType = 'xml' | 'json' | 'markdown' | 'text' | 'form' | 'graph' | 'comparator' | 'parallel-build-plan';
export interface ContentTab {
  id: string;
  type: ContentTabType;
  content: string;
  title: string;
  /**
   * Which tool this tab "belongs to" — set when a tab is created via
   * add_to_canvas (markdown), or implicitly defaulted to 'study-design'
   * for XML/form/graph/comparator tabs which are study-design-specific
   * today. ToolCanvas filters by this to render only its tool's tabs.
   * Undefined for legacy tabs created before this field existed; readers
   * should treat undefined as 'study-design' for backward compatibility.
   */
  targetTool?: ToolId;
  // For comparator tabs
  comparatorData?: {
    leftXml: string;
    rightXml: string;
    leftLabel: string;
    rightLabel: string;
  };
}

// Default titles for each tab type — defined at module scope so it's never recreated
const DEFAULT_TAB_TITLES: Record<ContentTabType, string> = {
  xml: 'ODM XML',
  json: 'Study Config',
  markdown: 'Study Design Canvas',
  text: 'Generated Content',
  form: 'Form Preview',
  graph: 'XML Visual',
  comparator: 'ODM Comparator',
  'parallel-build-plan': 'Build Plan'
};

// Shared by the autosave effect and the post-load seed below — both must
// use the exact same formula, or the seed silently fails to suppress the
// resave it's meant to prevent. File STATUS must participate: hashing only
// the count meant the processing→ready flip never re-saved, so blobs stayed
// frozen at 'processing' forever (eternal pills; false red pills after
// hydrate reconciliation).
function computeContentHash(sessionId: string, activeTabId: string | null, contentTabs: ContentTab[], uploadedFiles: UploadedFile[]): string {
  return `${sessionId}:${activeTabId}:${contentTabs.length}:${contentTabs.map(t => `${t.type}:${t.content?.length ?? 0}`).join(',')}:${uploadedFiles.map(f => `${f.id}:${f.status}`).join(',')}`;
}
function emitOdmBuildStep(step: string, xml: string, opts: {
  mergeMs: number;
  mergedCount?: number;
  isFragment: boolean;
}) {
  const validateStart = performance.now();
  const validation = validateThreePhOdm(xml);
  const validateMs = performance.now() - validateStart;
  const detail = {
    step,
    mergeMs: opts.mergeMs,
    validateMs,
    valid: validation.valid,
    xmlChars: xml.length,
    mergedCount: opts.mergedCount,
    summary: validation.summary,
    errorCount: validation.errors.length,
    warningCount: validation.warnings.length,
    isFragment: opts.isFragment
  };
  // Defer — addOrUpdateContentTab calls this inside a setContentTabs updater;
  // synchronous dispatch would setState during render in any live listeners.
  queueMicrotask(() => {
    window.dispatchEvent(new CustomEvent('crucible:odm-build-step', {
      detail
    }));
  });
}
interface UseContentTabsOptions {
  sessionId: string;
  isConnected: boolean;
  uploadedFiles: UploadedFile[];
  onSessionSaved?: (hasCanvas: boolean) => void;
  /** forSessionId = the session whose blob these files came from — consumers
   *  must drop restores for sessions that are no longer current. */
  onFilesRestored?: (files: UploadedFile[], forSessionId: string) => void;
}
export function useContentTabs({
  sessionId,
  isConnected,
  uploadedFiles,
  onSessionSaved,
  onFilesRestored
}: UseContentTabsOptions) {
  const {
    generateTabId
  } = useIdGenerator();
  const [contentTabs, setContentTabs] = useState<ContentTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Ref that mirrors activeTabId — used inside useCallback to avoid stale closure deps
  const activeTabIdRef = useRef<string | null>(null);
  activeTabIdRef.current = activeTabId;

  // Auto-snapshot: debounce timer + counter for XML drafts.
  // Only saves once XML has been stable for 3s — avoids flooding blob storage
  // when the AI makes multiple update_odm_xml calls in a single response.
  const xmlDraftCountRef = useRef(0);
  const xmlSnapshotTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Filenames already snapshotted this session — used to bump same-day build segment.
  const xmlSnapshotFilenamesRef = useRef<string[]>([]);

  // Get change context (optional - may not be available during SSR)
  const changeContext = useChangeContextOptional();

  // Helper to find an element's line range in formatted XML
  const findElementLineRange = useCallback((xml: string, diff: ElementDiff): {
    start: number;
    end: number;
  } | null => {
    const lines = xml.split('\n');
    const elementTag = diff.elementType;
    const oidAttr = diff.oid ? `OID="${diff.oid}"` : null;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      // Match the element's opening tag, checking for OID if available
      if (line.includes(`<${elementTag}`) && (!oidAttr || line.includes(oidAttr))) {
        // Found opening tag, now find closing tag or self-closing
        const startLine = i + 1; // 1-indexed for Monaco

        // Check if it's self-closing on the same line
        if (line.includes('/>')) {
          return {
            start: startLine,
            end: startLine
          };
        }

        // Look for closing tag
        for (let j = i; j < lines.length; j++) {
          if (lines[j].includes(`</${elementTag}>`)) {
            return {
              start: startLine,
              end: j + 1
            };
          }
        }
        // If no closing tag found, return just the start line
        return {
          start: startLine,
          end: startLine
        };
      }
    }
    return null;
  }, []);

  // Helper to emit XML changes to the ChangeContext
  const emitXmlChanges = useCallback((diffs: ElementDiff[], newXml: string, messageId?: string) => {
    if (!changeContext || diffs.length === 0) return;
    const changes: NewChange[] = diffs.map(diff_0 => {
      // Find line range for this element in the formatted XML
      const lineRange = findElementLineRange(newXml, diff_0);
      return {
        type: 'xml' as const,
        action: diff_0.changeType === 'added' ? 'add' as const : diff_0.changeType === 'removed' ? 'delete' as const : 'modify' as const,
        elementType: diff_0.elementType,
        oid: diff_0.oid || undefined,
        xpath: diff_0.path,
        summary: describeDiff(diff_0),
        sourceMessageId: messageId,
        xmlLineStart: lineRange?.start,
        xmlLineEnd: lineRange?.end
      };
    });
    changeContext.addChanges(changes);
    console.log(`Emitted ${changes.length} XML changes to ChangeContext`, changes);
  }, [changeContext, findElementLineRange]);

  // Helper function to add or update content tab
  const addOrUpdateContentTab = useCallback((type: ContentTabType, content: string, title?: string, isFragment?: boolean, messageId_0?: string, targetTool // Which tool the tab belongs to; ToolCanvas filters by this
  ?: ToolId) => {
    console.log('addOrUpdateContentTab called:', {
      type,
      contentLength: content?.length,
      title,
      isFragment,
      contentPreview: content?.substring(0, 100)
    });
    setContentTabs(prev => {
      // Check if we should update existing tab of same type
      const existingIndex = prev.findIndex(tab => tab.type === type);
      console.log('Existing tab index for type', type, ':', existingIndex, 'Total tabs:', prev.length);
      if (existingIndex !== -1) {
        const existingTab = prev[existingIndex];
        let newContent = content;
        // Markdown tabs accumulate multiple sections over a conversation
        // (see append branches below) — once the tab already has content,
        // `title` on a later call is only that NEW section's title, not
        // the whole document's. Captured before newContent is reassigned,
        // used at the title-assignment site below to keep the tab's
        // original title instead of letting each new section overwrite it.
        const isMarkdownWithExistingContent = type === 'markdown' && Boolean(existingTab.content);

        // For markdown tabs with messageId, use anchor-based section management
        if (type === 'markdown' && messageId_0) {
          // Track latest anchor for auto-scroll on tab switch
          pendingCanvasAnchorRef.current = `section-${messageId_0}`;

          // Use an HTML div with id attribute for proper anchor navigation
          // Prefix with 'section-' because IDs cannot start with a number
          const anchorMarker = `<div id="section-${messageId_0}" class="canvas-section"></div>`;
          if (existingTab.content && existingTab.content.includes(anchorMarker)) {
            // Replace existing section (streaming update for same messageId)
            // Match from anchor to either next anchor, separator, or end of string
            const escapedAnchor = anchorMarker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(`${escapedAnchor}[\\s\\S]*?(?=\\n\\n---\\n\\n<div\\s+id="section-|$)`, '');
            newContent = existingTab.content.replace(regex, `${anchorMarker}\n\n${content}`);
            console.log('Updating existing markdown section with messageId:', messageId_0, {
              foundAnchor: true,
              oldLength: existingTab.content.length,
              newLength: newContent.length
            });
          } else if (existingTab.content) {
            // Existing content but no anchor for this messageId - append as new section
            const separator = `\n\n---\n\n`;
            newContent = existingTab.content + separator + `${anchorMarker}\n\n${content}`;
            console.log('Appending new markdown section with messageId:', messageId_0);
          } else {
            // No existing content - just add anchor + content
            newContent = `${anchorMarker}\n\n${content}`;
            console.log('Creating first markdown section with messageId:', messageId_0);
          }
          // Emit canvas change for notification dots on inactive tabs
          if (changeContext) {
            setTimeout(() => {
              changeContext.addChange({
                type: 'canvas',
                action: existingTab.content?.includes(anchorMarker) ? 'modify' : 'add',
                sectionId: `section-${messageId_0}`,
                summary: title || 'Canvas updated',
                sourceMessageId: messageId_0
              });
            }, 0);
          }
        } else if (type === 'markdown' && existingTab.content) {
          // No messageId - just append (old behavior)
          newContent = existingTab.content + `\n\n---\n\n${content}`;
          if (changeContext) {
            setTimeout(() => {
              changeContext.addChange({
                type: 'canvas',
                action: 'modify',
                summary: title || 'Canvas updated'
              });
            }, 0);
          }
        }
        // Auto-detect fragments: if the AI sent XML without <?xml declaration
        // and an XML tab already has content, treat as fragment even if isFragment wasn't set
        else if (type === 'xml' && !isFragment && existingTab.content && !content.trimStart().startsWith('<?xml')) {
          console.log('[addOrUpdateContentTab] Auto-detected fragment (no <?xml declaration)');
          isFragment = true;
        }

        // For XML fragments, merge into existing content instead of replacing
        if (type === 'xml' && isFragment && existingTab.content) {
          const oldXml = existingTab.content;
          const mergeStart = performance.now();
          const mergeResult = mergeXmlFragment(existingTab.content, content);
          const mergeMs = performance.now() - mergeStart;
          newContent = mergeResult.result;
          if (mergeResult.error) {
            console.warn('[addOrUpdateContentTab] Fragment merge issue:', mergeResult.error);
          }

          // Run element-level diff and emit changes
          if (mergeResult.merged) {
            const diffs_0 = compareOdmXml(oldXml, newContent);
            if (diffs_0.length > 0) {
              setTimeout(() => emitXmlChanges(diffs_0, newContent, messageId_0), 0);
            }
            emitOdmBuildStep(title || 'Fragment merge', newContent, {
              mergeMs,
              mergedCount: mergeResult.mergedCount,
              isFragment: true
            });
          }
        }
        // For full XML replacement, compare with previous if available
        else if (type === 'xml' && !isFragment && existingTab.content) {
          const oldXml_0 = existingTab.content;
          const diffs_1 = compareOdmXml(oldXml_0, content);
          if (diffs_1.length > 0) {
            console.log('ODM element-level diff (full replace):', diffs_1);
            setTimeout(() => emitXmlChanges(diffs_1, content, messageId_0), 0);
          }
          emitOdmBuildStep(title || 'Study shell', content, {
            mergeMs: 0,
            isFragment: false
          });
        } else if (type === 'xml' && !isFragment) {
          emitOdmBuildStep(title || 'Study shell', content, {
            mergeMs: 0,
            isFragment: false
          });
        }

        // Auto-snapshot: debounced save — waits 3s after the last XML update
        // so a burst of update_odm_xml calls produces only one snapshot.
        if (type === 'xml' && newContent && sessionId) {
          const snapshotContent = newContent;

          // Cancel any pending snapshot — the latest content wins
          if (xmlSnapshotTimerRef.current) {
            clearTimeout(xmlSnapshotTimerRef.current);
          }
          xmlSnapshotTimerRef.current = setTimeout(() => {
            xmlDraftCountRef.current += 1;
            const draftNum = xmlDraftCountRef.current;
            const draftBasename = suggestOdmArtifactBasename(snapshotContent, {
              existingFilenames: xmlSnapshotFilenamesRef.current,
              studyCode: getActiveStudyCodeForRegistration() ?? undefined
            });
            const draftFilename = `${draftBasename}.xml`;
            xmlSnapshotFilenamesRef.current.push(draftFilename);
            const odmVersion = extractOdmVersionFromFilename(draftFilename);
            fetch(`/api/files/versions`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                study_id: 'default-study',
                session_id: sessionId,
                filename: draftFilename,
                content: snapshotContent,
                content_type: 'application/xml',
                parent_version: odmVersion ?? `0.${draftNum - 1}`,
                ...(odmVersion ? {
                  version: odmVersion
                } : {})
              })
            }).then(() => {
              window.dispatchEvent(new CustomEvent('crucible:files-changed'));
              console.log(`[Auto-snapshot] Saved XML draft ${odmVersion ?? `v0.${draftNum}`} (${draftFilename})`);
            }).catch(err => {
              console.warn('[Auto-snapshot] Failed to save XML draft:', err);
            });
          }, 3000);
        }
        console.log('Updating existing tab:', {
          tabId: existingTab.id,
          oldContentLength: existingTab.content?.length,
          newContentLength: newContent?.length
        });

        // Update existing tab content while preserving ID and position.
        //
        // Also accept a fresh `targetTool` from the caller when supplied.
        // Without this, an early add_to_canvas call that created the tab
        // with a fallback/undefined targetTool (e.g. modeRef.current was
        // unresolved at first dispatch) leaves the tab permanently tagged
        // for the wrong tool — every later add_to_canvas in the correct
        // mode silently preserves the stale tag. ToolCanvas then filters
        // the tab out and renders empty-state despite the in-memory
        // content being present.
        //
        // Override only when the caller supplies one — undefined means
        // "no opinion", preserve what's there.
        let updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          content: newContent,
          // isMarkdownWithExistingContent: keep the document's original
          // title — a later section's title must not overwrite it (a real
          // bug found 2026-07-11: after 3 sections accumulated, the tab
          // displayed only the 3rd section's title, making the earlier two
          // look like they'd vanished from any UI that shows the tab title
          // without opening and scrolling the full canvas).
          title: isMarkdownWithExistingContent ? updated[existingIndex].title : title || updated[existingIndex].title,
          ...(targetTool ? {
            targetTool
          } : {})
        };

        // If this is an XML update, also update form and graph tabs immediately
        if (type === 'xml') {
          updated = updated.map(tab_0 => {
            if (tab_0.type === 'form' || tab_0.type === 'graph') {
              return {
                ...tab_0,
                content: newContent
              };
            }
            return tab_0;
          });
        }

        // Ensure markdown tab stays at index 0
        if (type === 'markdown' && existingIndex !== 0) {
          const markdownTab = updated.splice(existingIndex, 1)[0];
          updated.unshift(markdownTab);
        }

        // Set as active tab if not already set
        if (!activeTabIdRef.current) {
          setActiveTabId(updated[existingIndex].id);
        }
        return updated;
      } else {
        // Add new tab with unique ID
        const tabId = generateTabId(type);

        // For markdown tabs with messageId, include anchor marker
        let tabContent = content;
        if (type === 'markdown' && messageId_0) {
          const anchorMarker_0 = `<div id="section-${messageId_0}" class="canvas-section"></div>`;
          tabContent = `${anchorMarker_0}\n\n${content}`;
          console.log('Creating new markdown tab with anchor for messageId:', messageId_0);
        }
        const newTab: ContentTab = {
          id: tabId,
          type,
          content: tabContent,
          title: title || DEFAULT_TAB_TITLES[type],
          // Leave targetTool unset when the caller omitted it — null means
          // unspecified, not Study Design. Read paths resolve unspecified tabs
          // via resolveTabTargetTool (active workspace, else Concierge).
          // add_to_canvas / Open-in always pass an explicit owner from ChatMode.
          ...(targetTool ? {
            targetTool
          } : {})
        };

        // Set as active tab if it's the first tab
        if (prev.length === 0 || !activeTabIdRef.current) {
          setActiveTabId(tabId);
        }

        // Markdown tabs always go first (index 0)
        if (type === 'markdown') {
          return [newTab, ...prev];
        }

        // For other tabs, insert after markdown if it exists
        const markdownIndex = prev.findIndex(tab_1 => tab_1.type === 'markdown');
        if (markdownIndex !== -1) {
          const updated_0 = [...prev];
          updated_0.splice(markdownIndex + 1, 0, newTab);
          return updated_0;
        }

        // Otherwise append at the end
        return [...prev, newTab];
      }
    });

    // Auto-scroll to newly added markdown content
    if (type === 'markdown' && messageId_0) {
      // Retry scroll until the section anchor exists in the DOM (tab switch + render delay)
      const anchor = `section-${messageId_0}`;
      let attempts = 0;
      const tryScroll = () => {
        attempts++;
        const el = document.getElementById(anchor);
        if (el) {
          window.dispatchEvent(new CustomEvent('scrollToCanvasSection', {
            detail: {
              anchor
            }
          }));
        } else if (attempts < 10) {
          setTimeout(tryScroll, 200);
        }
      };
      setTimeout(tryScroll, 150);
    }

    // Auto-create form and graph tabs when XML is added/updated
    if (type === 'xml' && content.trim()) {
      // Small delay to ensure XML tab is processed first
      setTimeout(() => {
        setContentTabs(prev_0 => {
          let updated_1 = [...prev_0];

          // Get the current XML content from the XML tab (may have been merged)
          const xmlTab = prev_0.find(tab_2 => tab_2.type === 'xml');
          const xmlContent = xmlTab?.content || content;

          // Handle form tab
          const formTabExists = prev_0.some(tab_3 => tab_3.type === 'form');
          if (!formTabExists) {
            const formTabId = generateTabId('form');
            updated_1.push({
              id: formTabId,
              type: 'form' as const,
              content: xmlContent,
              title: 'Form Preview'
            });
          } else {
            updated_1 = updated_1.map(tab_4 => tab_4.type === 'form' ? {
              ...tab_4,
              content: xmlContent
            } : tab_4);
          }

          // Handle graph tab
          const graphTabExists = updated_1.some(tab_5 => tab_5.type === 'graph');
          if (!graphTabExists) {
            const graphTabId = generateTabId('graph');
            updated_1.push({
              id: graphTabId,
              type: 'graph' as const,
              content: xmlContent,
              title: 'XML Graph'
            });
          } else {
            updated_1 = updated_1.map(tab_6 => tab_6.type === 'graph' ? {
              ...tab_6,
              content: xmlContent
            } : tab_6);
          }
          return updated_1;
        });
      }, 100);
    }
    // `sessionId` is the session the auto-snapshot POST above writes under;
    // without it here the callback kept whatever session was current when it
    // was last built, so XML drafts saved after a session switch were filed
    // against the previous session. `changeContext` adds no new churn —
    // emitXmlChanges (already a dep) is itself rebuilt on every change of it.
  }, [generateTabId, emitXmlChanges, changeContext, sessionId]);

  // `onFilesRestored` is a notification the load fires once when a blob turns
  // out to carry files — an event, not an input the load should re-run for.
  // The hook's props are a public surface: today's only caller passes a
  // useCallback([]), but an inline arrow from any future caller would make it
  // a dependency that changes every render, and this effect refetches the
  // session blob. Read through a ref instead, refreshed each commit so the
  // async body calls the current one.
  const onFilesRestoredRef = useRef(onFilesRestored);
  useEffect(() => {
    onFilesRestoredRef.current = onFilesRestored;
  });

  // Load session content from Azure blob, with versions-index fallback for XML
  useEffect(() => {
    // Cancellation guard: this load closes over sessionId. If the session
    // switches while the blob fetch is in flight, the cleanup below marks
    // this run stale and it must apply NOTHING — otherwise the previous
    // session's tabs/files land inside the new session (the 2026-06-11
    // cross-conversation leak path).
    let stale = false;
    const loadSessionContent = async () => {
      if (!isConnected || !sessionId) return;

      // Reset snapshot state for the new session
      xmlDraftCountRef.current = 0;
      xmlSnapshotFilenamesRef.current = [];
      if (xmlSnapshotTimerRef.current) {
        clearTimeout(xmlSnapshotTimerRef.current);
        xmlSnapshotTimerRef.current = null;
      }
      sessionEtagRef.current = null;
      setIsLoadingHistory(true);
      try {
        const sessionContent = (await apiClient.loadSessionContent(sessionId)) as SessionContent | null;
        if (stale) return;
        let restoredTabs: ContentTab[] = [];
        if (sessionContent) {
          sessionEtagRef.current = sessionContent.etag ?? null;
          // Restore content tabs
          if (sessionContent.tabs && sessionContent.tabs.length > 0) {
            restoredTabs = sessionContent.tabs;

            // Ensure form + graph tabs exist if XML tab has content
            const xmlTab_0 = restoredTabs.find(t => t.type === 'xml' && t.content?.trim());
            if (xmlTab_0) {
              if (!restoredTabs.some(t_0 => t_0.type === 'form')) {
                restoredTabs.push({
                  id: generateTabId('form'),
                  type: 'form',
                  content: xmlTab_0.content,
                  title: 'Form Preview'
                });
              }
              if (!restoredTabs.some(t_1 => t_1.type === 'graph')) {
                restoredTabs.push({
                  id: generateTabId('graph'),
                  type: 'graph',
                  content: xmlTab_0.content,
                  title: 'XML Graph'
                });
              }
            }
            setContentTabs(restoredTabs);
            if (sessionContent.active_tab_id) {
              setActiveTabId(sessionContent.active_tab_id);
            }
            console.log(`Restored ${restoredTabs.length} content tabs from session storage`);
          }
          // Restore uploaded files — only if the parent hasn't already loaded files
          const notifyFilesRestored = onFilesRestoredRef.current;
          if (sessionContent.uploaded_files && sessionContent.uploaded_files.length > 0 && notifyFilesRestored) {
            notifyFilesRestored(sessionContent.uploaded_files as UploadedFile[], sessionId);
            console.log(`Restored ${sessionContent.uploaded_files.length} uploaded files from session storage`);
          }

          // Seed the autosave dedup hash with what we JUST loaded — without
          // this, lastSaveHashRef starts at '' on every mount, so the very
          // next render (which sets contentTabs to this exact same,
          // unchanged data) always looks like a real change and forces one
          // guaranteed, wasted re-save of byte-identical content back to
          // blob storage. Confirmed via blob version history on a real
          // incident replay (2026-07-11): the canvas blob was re-saved 3
          // times with an identical md5 across ~26 minutes of an otherwise
          // idle session, one per remount. Uses the freshly-loaded data
          // directly (not the `uploadedFiles` prop, which settles onto this
          // same data only after the onFilesRestored round-trip above lands
          // in the parent and re-renders) so the seed is correct even
          // before that round-trip completes.
          lastSaveHashRef.current = computeContentHash(sessionId, sessionContent.active_tab_id ?? null, restoredTabs, sessionContent.uploaded_files as UploadedFile[] | undefined ?? []);
        }

        // XML-restore fallback was lifted out — see WorkspaceProvider, which
        // composes our `isLoadingHistory` + the already-loaded `fileVersions`
        // from useFileVersions and restores the latest XML from there. This
        // hook used to call apiClient.listFileVersions() here, which produced
        // a duplicate fetch alongside useFileVersions for every session load.
      } catch (error) {
        console.log('No previous session content found or failed to load:', error);
      } finally {
        // A superseded run must not clear the loading flag the new
        // session's run now owns.
        if (!stale) setIsLoadingHistory(false);
      }
    };
    loadSessionContent();
    return () => {
      stale = true;
    };
    // generateTabId is useCallback([]) from useIdGenerator — permanently
    // stable, so listing it cannot re-trigger the load.
  }, [isConnected, sessionId, generateTabId]);

  // Auto-save session content when tabs change — with content-hash dedup
  // to prevent save storms from provider re-renders that don't change data.
  const lastSaveHashRef = useRef<string>('');
  // Tracks the ETag of the last-known-good version of this session's blob —
  // seeded from GET on load, refreshed after every successful save. Sent as
  // base_etag on the next save so the backend can reject (409) a save that
  // would silently clobber a newer write (canvas-save-race fix, Incident A).
  const sessionEtagRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isConnected || !sessionId || isImpersonating()) return;
    if (contentTabs.length === 0 && uploadedFiles.length === 0) return;
    const hash = computeContentHash(sessionId, activeTabId, contentTabs, uploadedFiles);

    // Skip if nothing changed since last save
    if (hash === lastSaveHashRef.current) return;
    const debounceMs = contentTabs.some(t_2 => t_2.type === 'xml' && (t_2.content?.length ?? 0) > 0) ? 300 : 3000;
    const attemptSave = async (retryOnConflict: boolean): Promise<void> => {
      try {
        const response = (await apiClient.saveSessionContent(sessionId, contentTabs, activeTabId, uploadedFiles, sessionEtagRef.current)) as {
          etag?: string | null;
        } | undefined;
        sessionEtagRef.current = response?.etag ?? sessionEtagRef.current;
        lastSaveHashRef.current = hash;
        const hasCanvas = contentTabs.some(t_3 => t_3.type === 'markdown' || t_3.type === 'xml');
        onSessionSaved?.(hasCanvas);
      } catch (error_0) {
        const status = (error_0 as {
          status?: number;
        })?.status;
        if (status === 409 && retryOnConflict) {
          // Someone else's newer save already landed. Reload the current
          // etag and retry exactly once — the freshest write should still
          // win on a single-user reconnect/reload race; a real cross-tab
          // conflict will surface to the user on the second failure below.
          try {
            const fresh = (await apiClient.loadSessionContent(sessionId)) as {
              etag?: string | null;
            } | null;
            sessionEtagRef.current = fresh?.etag ?? null;
            await attemptSave(false);
            return;
          } catch {
            // fall through to the failure toast below
          }
        }
        console.warn('Failed to auto-save session content:', error_0);
        // Stable id so sonner updates the existing toast in place on repeated
        // failures instead of stacking a new one per debounced retry — a
        // sustained backend outage while the user keeps editing used to
        // queue dozens of toasts (visibleToasts=3), which felt like a
        // "toast storm" requiring a page refresh even though each one was
        // individually dismissable.
        toast.error('Could not save your changes', {
          id: `autosave-failed-${sessionId}`,
          description: status === 409 ? 'This canvas changed elsewhere. Your latest edits were not saved — reload to see the current version.' : 'A save just failed. Your work is still visible here, but keep this tab open until it succeeds.',
          duration: 10_000
        });
      }
    };
    const timer = setTimeout(() => {
      void attemptSave(true);
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [contentTabs, activeTabId, uploadedFiles, sessionId, isConnected, onSessionSaved]);

  // Clear all tabs
  const clearTabs = useCallback(async () => {
    setContentTabs([]);
    setActiveTabId(null);
    // Try to delete from remote (ignore errors)
    try {
      await apiClient.deleteSessionContent(sessionId);
    } catch (error_1) {
      console.log('Session may not exist in storage:', error_1);
    }
  }, [sessionId]);

  // Add comparator tab for ODM diff comparison
  const addComparatorTab = useCallback((leftXml: string, rightXml: string, leftLabel: string = 'Original', rightLabel: string = 'Modified', title_0: string = 'ODM Comparison') => {
    setContentTabs(prev_1 => {
      // Check if a comparator tab with the same title already exists
      const existingIndex_0 = prev_1.findIndex(tab_7 => tab_7.type === 'comparator' && tab_7.title === title_0);
      if (existingIndex_0 !== -1) {
        // Update existing tab with new data
        console.log(`Updating existing comparator tab: ${title_0}`);
        const updated_2 = [...prev_1];
        updated_2[existingIndex_0] = {
          ...updated_2[existingIndex_0],
          comparatorData: {
            leftXml,
            rightXml,
            leftLabel,
            rightLabel
          }
        };
        setActiveTabId(updated_2[existingIndex_0].id);
        return updated_2;
      }

      // Create new tab
      const tabId_0 = generateTabId('comparator');
      const newTab_0: ContentTab = {
        id: tabId_0,
        type: 'comparator',
        content: '',
        // Content is stored in comparatorData
        title: title_0,
        comparatorData: {
          leftXml,
          rightXml,
          leftLabel,
          rightLabel
        }
      };
      console.log(`Created comparator tab: ${title_0} (${leftLabel} vs ${rightLabel})`);
      setActiveTabId(tabId_0);
      return [...prev_1, newTab_0];
    });
  }, [generateTabId]);

  // Get active tab
  const activeTab = contentTabs.find(tab_8 => tab_8.id === activeTabId);

  // Pending canvas anchor — stores the latest messageId anchor when canvas
  // content is added while the canvas tab might not be active. Consumed by
  // tool-canvas.tsx on tab switch to auto-scroll to the new section.
  const pendingCanvasAnchorRef = useRef<string | null>(null);
  return useMemo(() => ({
    contentTabs,
    setContentTabs,
    activeTabId,
    setActiveTabId,
    activeTab,
    addOrUpdateContentTab,
    addComparatorTab,
    clearTabs,
    isLoadingHistory,
    pendingCanvasAnchorRef
  }), [contentTabs, activeTabId, activeTab, addOrUpdateContentTab, addComparatorTab, clearTabs, isLoadingHistory]);
}